// src/pages/resources/ChapterIntroTab.tsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid, // In MUI v6, this is Grid2
    TextField,
    Stack,
    LinearProgress,
    Card,
    CardContent,
    Tabs,
    Tab,
    InputAdornment,
    Alert,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Link as LinkIcon,
    Delete as DeleteIcon,
    Movie as VideoIcon,
    CheckCircle as CheckIcon,
    YouTube as YouTubeIcon,
    Title as TitleIcon,
    Description as DescriptionIcon,
    HelpOutline as QuestionIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
    setIntroductionByUrl,
    uploadIntroductionVideo,
    deleteIntroduction
} from '../../services/chapterService';
import { IntroductionDomain } from '../../models/ChapterResourcesDomain';

// --- Styles ---
const css = {
    primary: '#174143',
    primaryDark: '#0f2d2f',
    primaryLight: '#1f5961',
    primaryAccent: '#3d9ea4',
};

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 16,
    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
    border: `1px solid rgba(23, 65, 67, 0.1)`,
    height: '100%',
    position: 'relative',
    overflow: 'visible'
}));

const GradientButton = styled(Button)({
    background: `linear-gradient(to right, ${css.primary}, ${css.primaryLight})`,
    color: 'white',
    padding: '10px 24px',
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
        background: `linear-gradient(to right, ${css.primaryDark}, ${css.primary})`,
    },
});

function CloudUploadIcon(props: any) {
    return <UploadIcon {...props} />;
}

interface Props {
    chapterId: number;
    data: IntroductionDomain | null;
    refreshData: () => void;
}

export default function ChapterIntroTab({ chapterId, data, refreshData }: Props) {
    const [mode, setMode] = useState<'URL' | 'FILE'>('URL');

    // Form State
    const [urlForm, setUrlForm] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [meta, setMeta] = useState({
        title: '',
        description: '',
        question: ''
    });

    // Status States
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // --- EFFECT: Pre-fill data if available ---
    useEffect(() => {
        if (data) {
            setMeta({
                title: data.title || '',
                description: data.description || '',
                question: data.question || ''
            });

            // LOGIC FIX: Always show the URL if it exists (removed the clearing logic)
            if (data.url) {
                setUrlForm(data.url);
            } else {
                setUrlForm('');
            }
        } else {
            // Reset if no data passed
            setMeta({ title: '', description: '', question: '' });
            setUrlForm('');
        }
    }, [data]);

    // --- Actions ---

    const handleUrlSubmit = async () => {
        if (!urlForm || !meta.title) {
            setMsg({ type: 'error', text: 'URL and Title are required.' });
            return;
        }
        setLoading(true);
        setMsg(null);
        try {
            await setIntroductionByUrl(chapterId, {
                title: meta.title,
                url: urlForm,
                description: meta.description,
                question: meta.question,
                displayOrder: 1
            });
            setMsg({ type: 'success', text: 'Introduction updated successfully!' });
            refreshData();
        } catch (e) {
            setMsg({ type: 'error', text: 'Failed to update URL.' });
        } finally {
            setLoading(false);
        }
    };

    const handleFileSubmit = async () => {
        if (!file || !meta.title) {
            setMsg({ type: 'error', text: 'Video File and Title are required.' });
            return;
        }
        setLoading(true);
        setProgress(0);
        setMsg(null);
        try {
            await uploadIntroductionVideo(
                chapterId,
                file,
                {
                    title: meta.title,
                    description: meta.description,
                    question: meta.question
                },
                (percent) => {
                    setProgress(percent);
                }
            );
            setMsg({ type: 'success', text: 'Video uploaded successfully!' });
            refreshData();
            setFile(null);
        } catch (e) {
            setMsg({ type: 'error', text: 'Failed to upload video.' });
        } finally {
            setLoading(false);
            setProgress(0);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to remove the introduction video?")) return;
        setLoading(true);
        try {
            await deleteIntroduction(chapterId, true);
            setMsg({ type: 'success', text: 'Introduction removed.' });
            setMeta({ title: '', description: '', question: '' });
            setUrlForm('');
            refreshData();
        } catch (e) {
            setMsg({ type: 'error', text: 'Delete failed.' });
        } finally {
            setLoading(false);
        }
    };

    // --- Render Helpers ---

    const renderCommonFields = () => (
        <Stack spacing={2} sx={{ mt: 2, mb: 3 }}>
            <TextField
                fullWidth
                required
                label="Video Title"
                value={meta.title}
                onChange={(e) => setMeta({ ...meta, title: e.target.value })}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><TitleIcon color="action"/></InputAdornment>
                }}
            />
            <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                placeholder="Brief overview of the content..."
                value={meta.description}
                onChange={(e) => setMeta({ ...meta, description: e.target.value })}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><DescriptionIcon color="action"/></InputAdornment>
                }}
            />
            <TextField
                fullWidth
                label="Introductory Question"
                placeholder="e.g. What is the first law of thermodynamics?"
                value={meta.question}
                onChange={(e) => setMeta({ ...meta, question: e.target.value })}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><QuestionIcon color="action"/></InputAdornment>
                }}
            />
        </Stack>
    );

    const renderCurrentVideo = () => {
        if (!data || !data.url) return null;

        return (
            <StyledCard sx={{ overflow: 'hidden' }}>
                <Box sx={{ background: css.primary, p: 2, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VideoIcon />
                    <Typography variant="h6" fontWeight={700}>Current Introduction</Typography>
                </Box>
                <CardContent>
                    <Box sx={{
                        bgcolor: '#000',
                        borderRadius: 2,
                        overflow: 'hidden',
                        aspectRatio: '16/9',
                        mb: 2
                    }}>
                        <video controls style={{ width: '100%', height: '100%' }}>
                            <source src={data.url} />
                            Your browser does not support the video tag.
                        </video>
                    </Box>

                    <Stack spacing={1}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                            <Typography variant="body1" fontWeight={600} color={css.primaryDark}>
                                {data.title}
                            </Typography>
                        </Box>
                        {data.description && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">Description</Typography>
                                <Typography variant="body2">{data.description}</Typography>
                            </Box>
                        )}
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteIcon />}
                            onClick={handleDelete}
                            disabled={loading}
                            sx={{ mt: 1 }}
                        >
                            Remove Video
                        </Button>
                    </Stack>
                </CardContent>
            </StyledCard>
        );
    };

    return (
        <Box sx={{ width: '100%', animation: 'fadeIn 0.3s ease-in' }}>
            {msg && (
                <Alert severity={msg.type} sx={{ mb: 3 }} onClose={() => setMsg(null)}>
                    {msg.text}
                </Alert>
            )}

            <Grid container spacing={4}>
                {/* SYNTAX FIX: Using 'size' prop instead of 'item xs' */}
                <Grid size={{ xs: 12, md: data?.url ? 7 : 12 }}>
                    <StyledCard>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 2, bgcolor: '#fafafa' }}>
                            <Tabs
                                value={mode}
                                onChange={(_, v) => setMode(v)}
                                textColor="primary"
                                indicatorColor="primary"
                                sx={{ '& .MuiTab-root': { fontWeight: 600 } }}
                            >
                                <Tab label="External URL (YouTube)" value="URL" icon={<YouTubeIcon fontSize="small"/>} iconPosition="start" />
                                <Tab label="Upload File" value="FILE" icon={<CloudUploadIcon fontSize="small"/>} iconPosition="start"/>
                            </Tabs>
                        </Box>

                        <CardContent sx={{ p: 4 }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={700}>
                                CONTENT DETAILS
                            </Typography>
                            {renderCommonFields()}

                            <Typography variant="subtitle2" color="text.secondary" fontWeight={700} sx={{ mb: 2 }}>
                                VIDEO SOURCE
                            </Typography>

                            {mode === 'URL' && (
                                <Stack spacing={3}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Video URL"
                                        placeholder="https://..."
                                        value={urlForm}
                                        onChange={(e) => setUrlForm(e.target.value)}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start"><LinkIcon color="action"/></InputAdornment>
                                        }}
                                        variant="outlined"
                                        helperText="Direct link to MP4 or supported video stream."
                                    />
                                    <Box>
                                        <GradientButton
                                            onClick={handleUrlSubmit}
                                            disabled={loading}
                                            startIcon={loading ? null : <CheckIcon />}
                                        >
                                            {loading ? 'Updating...' : 'Set Introduction'}
                                        </GradientButton>
                                    </Box>
                                </Stack>
                            )}

                            {mode === 'FILE' && (
                                <Stack spacing={3}>
                                    <Box
                                        sx={{
                                            border: `2px dashed ${css.primaryAccent}`,
                                            borderRadius: 2,
                                            p: 4,
                                            textAlign: 'center',
                                            bgcolor: '#f4fbfb',
                                            cursor: 'pointer',
                                            transition: '0.2s',
                                            '&:hover': { bgcolor: '#e0f2f2' }
                                        }}
                                        component="label"
                                    >
                                        <input type="file" hidden accept="video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                                        <CloudUploadIcon sx={{ fontSize: 48, color: css.primaryAccent, mb: 1 }} />
                                        <Typography variant="h6" color={css.primaryDark}>
                                            {file ? file.name : "Click to Select Video File"}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "Max size depends on server config"}
                                        </Typography>
                                    </Box>

                                    {loading && (
                                        <Box sx={{ width: '100%' }}>
                                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                <Typography variant="caption" fontWeight={600}>Uploading...</Typography>
                                                <Typography variant="caption">{progress}%</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
                                        </Box>
                                    )}

                                    <Box>
                                        <GradientButton
                                            onClick={handleFileSubmit}
                                            disabled={loading}
                                            startIcon={loading ? null : <CloudUploadIcon />}
                                        >
                                            {loading ? 'Uploading...' : 'Upload & Save'}
                                        </GradientButton>
                                    </Box>
                                </Stack>
                            )}
                        </CardContent>
                    </StyledCard>
                </Grid>

                {data?.url && (
                    /* SYNTAX FIX: Using 'size' prop instead of 'item xs' */
                    <Grid size={{ xs: 12, md: 5 }}>
                        {renderCurrentVideo()}
                    </Grid>
                )}
            </Grid>
        </Box>
    );
}